import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import * as Styled from './ControlPanel.styles';

interface ControlPanelProps {
  onEditMode: () => void;
  onPlayMode: () => void;
  onBlowUp: () => void;
  onGravity: () => void;
  onAddWords: () => void;
  onExport: () => void;
  onExportData: () => void;
  deleteShape: () => void;
  onMoonWalk: () => void;
  onAddShapes: () => void;
  onAddStickers: () => void;
  onClearAll: () => void;
  onRandomBackground: () => void;
  isEditMode: boolean;
  shapeSelected: boolean;
  isAddingItems: boolean;
  wordCount?: string;
  stickerCount?: string;
  backgroundCount?: string;
}

const spring = {
  type: 'spring',
  stiffness: 700,
  damping: 30
};

export const ControlPanel = ({
  onEditMode,
  onPlayMode,
  onBlowUp,
  onGravity,
  onAddWords,
  onMoonWalk,
  onExport,
  onExportData,
  onAddShapes,
  onAddStickers,
  onClearAll,
  onRandomBackground,
  deleteShape,
  isEditMode,
  shapeSelected,
  isAddingItems,
  wordCount,
  stickerCount = '',
  backgroundCount = ''
}: ControlPanelProps) => {
  const motionDivRef = useRef<HTMLDivElement>(null);

  const isSmallScreen = window.innerWidth <= 1024;

  // const [isOn, setIsOn] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(!isSmallScreen);

  const [dragConstraints, setDragConstraints] = useState<any>(null);

  const toggleSwitch = () => {
    // setIsOn(!isOn);
    if (isEditMode) {
      onPlayMode();
    } else {
      onEditMode();
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  const getConstraints = useCallback(() => {
    setTimeout(() => {
      const motionDiv = motionDivRef.current;
      if (motionDiv) {
        const motionDivBounds = motionDiv.getBoundingClientRect();
        const windowConstraints = {
          left: (window.innerWidth - motionDivBounds.width - 20) * -1,
          top: -0,
          right: 0,
          bottom: window.innerHeight - motionDivBounds.height
        };
        setDragConstraints(windowConstraints);
      }
    }, 1000);
  }, []);

  useEffect(() => {
    getConstraints();
  }, [motionDivRef, isMenuOpen, getConstraints]);

  return (
    <Styled.ControlPanel
      ref={motionDivRef}
      drag={!isSmallScreen}
      dragConstraints={dragConstraints || {}}
      dragElastic={0.001}
    >
      <Styled.Header>
        <div className="header-switch">
          <div className="switch" data-ison={isEditMode} data-isdisabled={isAddingItems} onClick={toggleSwitch}>
            <motion.div className="handle" layout transition={spring} />
          </div>
          {isEditMode ? <h1>Edit</h1> : <h1>Play</h1>}
        </div>
        <Styled.TopFooter>
          <PanelFooter />
        </Styled.TopFooter>
        <Styled.MenuToggle onClick={toggleMenu}>
          {isMenuOpen ? 'Close' : 'Menu'}
          {isMenuOpen ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10.6739 11.9999L3.83691 18.8369L5.16291 20.1629L11.9999 13.3259L18.8369 20.1629L20.1629 18.8369L13.3259 11.9999L20.1629 5.16291L18.8369 3.83691L11.9999 10.6739L5.16291 3.83691L3.83691 5.16291L10.6739 11.9999Z"
                fill="#FBF8F0"
              />
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <motion.path
                d="M4 6H20M4 12H20M4 18H20"
                stroke="#FBF8F0"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                initial={{ pathLength: 0, pathOffset: 1 }}
                animate={{ pathLength: 1, pathOffset: 0 }}
                transition={{ duration: 0.5 }}
              />
            </svg>
          )}
        </Styled.MenuToggle>
      </Styled.Header>

      <AnimatePresence onExitComplete={getConstraints} initial={false}>
        {isMenuOpen && (
          <Styled.Content
            style={{ overflow: 'hidden' }}
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            transition={{ duration: isSmallScreen ? 0.15 : 0.5 }}
            exit={{ height: 0 }}
            key={'container'}
          >
            <Styled.Section>
              <h2>Have Fun</h2>
              <Styled.FunButton
                disabled={isAddingItems}
                onClick={() => {
                  onBlowUp();
                  isSmallScreen && setIsMenuOpen(false);
                }}
              >
                Bring Chaos
              </Styled.FunButton>
              <Styled.FunButton
                disabled={isAddingItems}
                onClick={() => {
                  onGravity();
                  isSmallScreen && setIsMenuOpen(false);
                }}
              >
                Make it fall
              </Styled.FunButton>
              <Styled.FunButton
                disabled={isAddingItems}
                onClick={() => {
                  onMoonWalk();
                  isSmallScreen && setIsMenuOpen(false);
                }}
              >
                Low Gravity
              </Styled.FunButton>
            </Styled.Section>
            <Styled.Section>
              <h2>Create a Message</h2>
              <Styled.EditButton
                disabled={isAddingItems}
                onClick={() => {
                  onAddWords();
                  isSmallScreen && setIsMenuOpen(false);
                }}
              >
                <span>Drop Words</span>
                <span className="subtext">{wordCount}</span>
              </Styled.EditButton>
              <Styled.EditButton
                disabled={isAddingItems}
                onClick={() => {
                  onAddStickers();
                  isSmallScreen && setIsMenuOpen(false);
                }}
              >
                Drop Stickers
                <span className="subtext">{stickerCount}</span>
              </Styled.EditButton>
              <Styled.EditButton
                disabled={isAddingItems}
                onClick={() => {
                  onAddShapes();
                  isSmallScreen && setIsMenuOpen(false);
                }}
              >
                Drop Shapes
              </Styled.EditButton>
              <Styled.EditButton
                disabled={isAddingItems}
                onClick={() => {
                  onRandomBackground();
                }}
              >
                Choose Background
                <span className="subtext">{backgroundCount}</span>
              </Styled.EditButton>
            </Styled.Section>
            <Styled.Section>
              {/* <Styled.Separator /> */}
              <h2>Clean Up</h2>
              <Styled.Button
                disabled={isAddingItems}
                onClick={() => {
                  onClearAll();
                  // isSmallScreen && setIsMenuOpen(false);
                }}
              >
                <span>Clear All</span>
                <svg
                  stroke="currentColor"
                  fill="currentColor"
                  strokeWidth="0"
                  viewBox="0 0 24 24"
                  height="200px"
                  width="200px"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M8.58564 8.85461L3.63589 13.8044L8.83021 18.9987L9.99985 18.998V18.9967H11.1714L14.9496 15.2186L8.58564 8.85461ZM9.99985 7.44039L16.3638 13.8044L19.1922 10.9759L12.8283 4.61197L9.99985 7.44039ZM13.9999 18.9967H20.9999V20.9967H11.9999L8.00229 20.9992L1.51457 14.5115C1.12405 14.1209 1.12405 13.4878 1.51457 13.0972L12.1212 2.49065C12.5117 2.10012 13.1449 2.10012 13.5354 2.49065L21.3136 10.2688C21.7041 10.6593 21.7041 11.2925 21.3136 11.683L13.9999 18.9967Z"></path>
                </svg>
              </Styled.Button>
              <Styled.DeleteButton
                onClick={() => {
                  deleteShape();
                  isSmallScreen && setIsMenuOpen(false);
                }}
                disabled={!shapeSelected}
              >
                <span>Delete Selected</span>
                <svg
                  stroke="currentColor"
                  fill="none"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </Styled.DeleteButton>
            </Styled.Section>
            <Styled.Section>
              <h2>Export</h2>
              <Styled.Button
                disabled={isAddingItems}
                onClick={() => {
                  onExport();
                  isSmallScreen && setIsMenuOpen(false);
                }}
              >
                <span>Save as Image</span>
                <svg
                  stroke="currentColor"
                  fill="currentColor"
                  strokeWidth="0"
                  viewBox="0 0 512 512"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M416 199.5h-91.4V64H187.4v135.5H96l160 158.1 160-158.1zM96 402.8V448h320v-45.2H96z"></path>
                </svg>
              </Styled.Button>
              {/* <Styled.Button
                disabled={isAddingItems}
                onClick={() => {
                  onExportData();
                  isSmallScreen && setIsMenuOpen(false);
                }}
              >
                <span>Export Data</span>
                <svg
                  stroke="currentColor"
                  fill="currentColor"
                  strokeWidth="1.5"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g id="Export">
                    <g>
                      <path d="M5.552,20.968a2.577,2.577,0,0,1-2.5-2.73c-.012-2.153,0-4.306,0-6.459a.5.5,0,0,1,1,0c0,2.2-.032,4.4,0,6.6.016,1.107.848,1.589,1.838,1.589H18.353A1.546,1.546,0,0,0,19.825,19a3.023,3.023,0,0,0,.1-1.061V11.779h0a.5.5,0,0,1,1,0c0,2.224.085,4.465,0,6.687a2.567,2.567,0,0,1-2.67,2.5Z"></path>
                      <path d="M12.337,3.176a.455.455,0,0,0-.311-.138c-.015,0-.028,0-.043-.006s-.027,0-.041.006a.457.457,0,0,0-.312.138L7.961,6.845a.5.5,0,0,0,.707.707l2.816-2.815V15.479a.5.5,0,0,0,1,0V4.737L15.3,7.552a.5.5,0,0,0,.707-.707Z"></path>
                    </g>
                  </g>
                </svg>
              </Styled.Button> */}
            </Styled.Section>
            <Styled.BottomFooter>
              <PanelFooter />
            </Styled.BottomFooter>
          </Styled.Content>
        )}
      </AnimatePresence>
    </Styled.ControlPanel>
  );
};

const PanelFooter = () => (
  <Styled.Footer>
    <a href="https://www.instagram.com/p/C1cyvYguruq/" target="_blank" rel="noreferrer">
      <svg width="91" height="20" viewBox="0 0 91 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M2.48914 10.0949V13.2441H1.09298V5.43404H3.32894C4.46266 5.43404 5.01903 5.85394 5.01903 7.10314V8.38382C5.01903 9.57004 4.43117 10.0949 3.32894 10.0949H2.48914ZM2.48914 8.92969H2.98252C3.4759 8.92969 3.63337 8.76173 3.63337 8.10039V7.36557C3.63337 6.73573 3.4759 6.54677 2.98252 6.54677H2.48914V8.92969ZM10.0872 11.2181C10.0872 12.6038 9.46781 13.3386 8.13464 13.3386H7.92469C6.57052 13.3386 5.96167 12.6038 5.96167 11.2181V7.44955C5.96167 6.10588 6.56002 5.35006 7.91419 5.35006H8.12414C9.5098 5.35006 10.0872 6.12687 10.0872 7.43905V11.2181ZM8.6805 7.46005C8.6805 6.79871 8.42856 6.56777 8.01916 6.56777C7.59927 6.56777 7.36832 6.79871 7.36832 7.46005V11.2286C7.36832 11.8165 7.58877 12.1104 8.02966 12.1104C8.46006 12.1104 8.6805 11.8165 8.6805 11.2286V7.46005ZM14.6043 10.5463C14.4363 9.48606 14.3313 8.79322 14.2998 8.37333C14.2578 8.79322 14.1529 9.48606 13.9954 10.5463L13.5755 13.2441H12.0744L10.7727 5.43404H12.1479L12.6517 9.01367C12.7882 10.0214 12.8932 10.8927 12.9352 11.3021C12.9667 10.8927 13.0821 10.0004 13.2186 9.01367L13.7225 5.43404H14.9297L15.4441 9.01367C15.5805 9.96894 15.696 10.8822 15.7275 11.3021C15.759 10.8927 15.8744 10.0109 16.0109 9.01367L16.5148 5.43404H17.8165L16.5148 13.2441H15.0137L14.6043 10.5463ZM22.0658 5.43404V6.65175H19.9978V8.58328H21.7194V9.83247H19.9978V11.9949H22.1288V13.2441H18.6331V5.43404H22.0658ZM24.4369 8.66726H25.0143C25.4867 8.66726 25.6546 8.39432 25.6546 7.89044V7.21861C25.6546 6.68324 25.4867 6.48379 25.0143 6.48379H24.4369V8.66726ZM24.4369 13.2441H23.0618V5.43404H25.5182C26.5259 5.43404 27.0298 5.87494 27.0298 6.91418V7.76448C27.0298 8.58328 26.5994 8.91919 26.2425 9.02417C26.6519 9.18163 27.0298 9.51755 27.0298 10.3154V12.2994C27.0298 12.7088 27.0823 13.0132 27.1558 13.1917V13.2441H25.7911C25.7176 13.1182 25.6546 12.8872 25.6546 12.3519V10.5463C25.6546 10.0109 25.4762 9.77998 25.0143 9.77998H24.4369V13.2441ZM31.5791 5.43404V6.65175H29.5111V8.58328H31.2327V9.83247H29.5111V11.9949H31.6421V13.2441H28.1465V5.43404H31.5791ZM32.5751 5.43404H34.7795C36.0392 5.43404 36.5431 5.93792 36.5431 7.22911V11.4071C36.5431 12.6668 36.0392 13.2441 34.7795 13.2441H32.5751V5.43404ZM33.9712 12.0579H34.4646C34.9685 12.0579 35.1364 11.785 35.1364 11.2391V7.36557C35.1364 6.78821 34.979 6.54677 34.4856 6.54677H33.9712V12.0579ZM39.2692 5.43404H41.6417C42.8489 5.43404 43.2583 5.91693 43.2583 6.95617V7.649C43.2583 8.52029 42.8908 8.83521 42.5339 8.97168C42.9328 9.11865 43.3737 9.44407 43.3737 10.3888V11.5331C43.3737 12.5933 42.8174 13.2441 41.6417 13.2441H39.2692V5.43404ZM40.6444 6.48379V8.55178H41.2742C41.7361 8.55178 41.9041 8.32084 41.9041 7.80647V7.18712C41.9041 6.67274 41.7361 6.48379 41.2637 6.48379H40.6444ZM40.6444 9.67501V12.1419H41.2952C41.7886 12.1419 41.9566 11.9425 41.9566 11.3861V10.4413C41.9566 9.89546 41.7676 9.67501 41.2742 9.67501H40.6444ZM45.2477 10.2524L43.841 5.43404H45.2267L45.9615 8.55178L46.6963 5.43404H48.019L46.6438 10.2839V13.2441H45.2477V10.2524Z"
          fill="#FBF8F0"
        />
        <path
          d="M90.487 16.7157L89.3842 8.44885C89.3734 8.37223 89.3205 8.30997 89.2521 8.28483L74.0972 0.0323252C74.0648 0.0119723 74.0263 0 73.9867 0C73.9783 0 73.9698 0 73.9614 0.00119723L71.0386 0.359169C70.9353 0.371141 70.8584 0.458539 70.8584 0.562698V1.52168L69.2222 0.632137C69.1885 0.60939 69.1489 0.597418 69.108 0.597418C69.0996 0.597418 69.0912 0.597418 69.0828 0.598615L58.6792 1.87127C58.5759 1.88324 58.499 1.97064 58.499 2.0748V3.03378L56.8352 2.12987C56.8087 2.1179 56.7787 2.10952 56.7487 2.10952C56.7403 2.10952 56.7319 2.10952 56.7234 2.11072L55.438 2.26755C55.3347 2.27953 55.2578 2.36692 55.2578 2.47108V10.8732C55.2578 10.9319 55.283 10.9882 55.3275 11.0265C55.3503 11.0468 55.3779 11.06 55.4068 11.0684L69.3339 19.2479C69.5657 19.4059 69.8589 19.4885 70.2 19.4885C70.2998 19.4885 70.4043 19.4813 70.5124 19.4682L70.7971 19.4334C71.3605 19.364 71.7942 19.1257 72.0849 18.739L72.652 19.0467C72.6868 19.0731 72.7289 19.0874 72.7733 19.0874H72.7853L74.0828 18.9282C74.1849 18.9162 74.2618 18.8288 74.2618 18.7259V18.7115L74.4816 18.8312C74.5141 18.8528 74.5525 18.8636 74.5921 18.8636C74.5921 18.8636 74.6017 18.8636 74.6042 18.8636C74.6078 18.8636 75.7767 18.7199 75.7767 18.7199C75.8788 18.7079 75.9557 18.6205 75.9557 18.5176V18.0423L76.8867 18.5451C76.9143 18.5595 76.9456 18.5702 76.978 18.5702H76.9924L78.0508 18.4409C78.1529 18.429 78.2298 18.3416 78.2298 18.2386V18.2266L78.4544 18.3475C78.4857 18.3667 78.5217 18.3763 78.5602 18.3763H78.5722L83.1108 17.8208C83.2129 17.8088 83.2898 17.7214 83.2898 17.6184V17.44L83.7728 17.701C83.8016 17.7166 83.834 17.7262 83.8689 17.7262H83.8833L84.4071 17.6615C84.4984 17.6508 84.5717 17.5789 84.5849 17.4879L84.6113 17.3L85.0354 17.5286C85.0762 17.5502 85.1123 17.5682 85.1543 17.5682H85.1687L85.4955 17.5286L85.5159 17.5262L86.3869 17.4197L86.8025 17.3694L87.796 17.2473C87.8921 17.2353 87.9654 17.1611 87.9738 17.0653L88.0231 16.5649L88.9433 17.0653C88.9734 17.0809 89.007 17.0928 89.0406 17.0928H89.055L90.3068 16.9396C90.3609 16.9324 90.4102 16.9049 90.4438 16.8618C90.4774 16.8187 90.4918 16.7636 90.4846 16.7097L90.487 16.7157ZM58.2515 10.4638L55.5341 10.7966V2.53094L56.6826 2.39087V9.41741C56.6826 9.43657 56.691 9.45572 56.7054 9.46769C56.7174 9.47847 56.7343 9.48445 56.7511 9.48445C56.7535 9.48445 56.7571 9.48445 56.7595 9.48445L58.2527 9.30128V10.4638H58.2515ZM59.9226 10.2591L58.7741 10.3991V2.13466L59.9226 1.99458V10.2591V10.2591ZM63.732 2.60996L62.7121 2.73447C62.6785 2.73806 62.652 2.768 62.652 2.80152V9.92384L61.5144 10.0627V2.95835C61.5144 2.9392 61.506 2.92004 61.4916 2.90807C61.4795 2.8973 61.4627 2.89131 61.4459 2.89131C61.4435 2.89131 61.4399 2.89131 61.4375 2.89131L60.4332 3.01462V1.93113L63.7308 1.52767V2.61116L63.732 2.60996ZM67.3817 2.16339L66.3618 2.28791C66.3281 2.2915 66.3017 2.32143 66.3017 2.35495V9.47727L65.164 9.61615V2.51179C65.164 2.49263 65.1556 2.47348 65.1412 2.4615C65.1292 2.45073 65.1124 2.44474 65.0956 2.44474C65.0932 2.44474 65.0896 2.44474 65.0872 2.44474L64.0828 2.56806V1.48456L67.3805 1.0811V2.16459L67.3817 2.16339ZM67.8935 1.01765L69.042 0.877569V7.90411C69.042 7.92327 69.0504 7.94242 69.0648 7.95439C69.0768 7.96517 69.0936 7.97116 69.1104 7.97116C69.1128 7.97116 69.1164 7.97116 69.1188 7.97116L70.6121 7.78798V8.95049L67.8947 9.28332V1.01884L67.8935 1.01765ZM72.2399 13.6532L71.1143 13.7909V12.3877C71.1143 12.2453 71.1143 11.8143 70.695 11.8143C70.6674 11.8143 70.6373 11.8167 70.6049 11.8202C70.2253 11.8669 70.1472 12.122 70.1472 12.4895V17.4808C70.1472 17.9752 70.3718 18.0782 70.5605 18.0782C70.5833 18.0782 70.6073 18.077 70.6325 18.0734C70.8512 18.047 71.1131 17.9058 71.1131 17.3898V15.4957L72.2387 15.3581V17.2233C72.2387 18.3894 71.7426 19.0419 70.7635 19.1617L70.4788 19.1964C70.3815 19.2084 70.2877 19.2143 70.1988 19.2143C69.7459 19.2143 69.4156 19.0551 69.2162 18.739C69.0564 18.4864 68.9747 18.132 68.9747 17.6831V12.5553C68.9747 11.9496 69.1669 10.9092 70.4499 10.7523L70.7455 10.7164C70.8584 10.702 70.9665 10.6961 71.065 10.6961C72.0958 10.6961 72.2375 11.4228 72.2375 12.2082V13.6496L72.2399 13.6532ZM71.1335 8.88704V0.621362L73.9194 0.280152V1.36364L72.342 1.5564C72.3084 1.55999 72.282 1.58992 72.282 1.62344V3.82635C72.282 3.8455 72.2904 3.86466 72.3048 3.87663C72.3168 3.88741 72.3336 3.89339 72.3504 3.89339C72.3528 3.89339 72.3565 3.89339 72.3589 3.89339L73.5674 3.74493V4.8847L72.342 5.03435C72.3084 5.03794 72.282 5.06787 72.282 5.1014V7.52938C72.282 7.54853 72.2904 7.56769 72.3048 7.57966C72.3168 7.59044 72.3336 7.59642 72.3504 7.59642C72.3528 7.59642 72.3565 7.59642 72.3589 7.59642L73.9879 7.39649V8.53625L71.1335 8.88584V8.88704ZM73.9903 18.6684L72.8418 18.8085V10.5428L73.9903 10.4027V18.6684ZM77.9583 18.1823L77.0309 18.2961L75.8175 13.67C75.8091 13.64 75.7827 13.6197 75.7514 13.6197C75.749 13.6197 75.7454 13.6197 75.743 13.6197C75.7094 13.6245 75.6842 13.6532 75.6842 13.6867V18.4601L74.6606 18.5858V10.3201L75.701 10.1932L76.7894 14.5068C76.7966 14.5368 76.8242 14.5583 76.8555 14.5583C76.8579 14.5583 76.8615 14.5583 76.8639 14.5583C76.8975 14.5535 76.9239 14.5248 76.9239 14.4913V10.0448L77.9595 9.91785V18.1835L77.9583 18.1823ZM81.483 17.7513L78.6286 18.1009V9.83524L81.4145 9.49403V10.5775L79.8372 10.7703C79.8035 10.7739 79.7771 10.8038 79.7771 10.8373V13.039C79.7771 13.0582 79.7855 13.0773 79.7999 13.0893C79.812 13.1001 79.8288 13.1061 79.8456 13.1061C79.848 13.1061 79.8516 13.1061 79.854 13.1061L81.0625 12.9576V14.0974L79.8372 14.247C79.8035 14.2506 79.7771 14.2806 79.7771 14.3141V16.7421C79.7771 16.7612 79.7855 16.7804 79.7999 16.7923C79.812 16.8031 79.8288 16.8091 79.8456 16.8091C79.848 16.8091 79.8516 16.8091 79.854 16.8091L81.483 16.6092V17.7489V17.7513ZM86.2932 17.1623L85.224 17.2928V11.9376C85.224 11.9017 85.1963 11.8729 85.1603 11.8693C85.1591 11.8693 85.1567 11.8693 85.1555 11.8693C85.1219 11.8693 85.093 11.8945 85.0882 11.928L84.3218 17.4029L83.9277 17.4508L83.1529 12.1806C83.1481 12.1471 83.1192 12.1231 83.0856 12.1231C83.0844 12.1231 83.082 12.1231 83.0808 12.1231C83.0448 12.1255 83.0171 12.1555 83.0171 12.1914V17.5634L82.0044 17.6867V9.4222L83.5265 9.23543L84.1308 13.5323C84.1356 13.5658 84.1644 13.591 84.198 13.591C84.2317 13.591 84.2605 13.5658 84.2653 13.5323L84.884 9.06902L86.2908 8.89661V17.1623H86.2932ZM89.1055 16.8187L88.9421 15.3269C88.9397 15.309 88.9313 15.2922 88.9169 15.2814C88.9049 15.2719 88.8905 15.2671 88.8748 15.2671C88.8724 15.2671 88.8688 15.2671 88.8664 15.2671L87.9222 15.382C87.8909 15.3856 87.8657 15.4107 87.8621 15.4431L87.7083 16.9899L86.7004 17.1132L87.718 8.72302L89.1223 8.55061L90.2071 16.6834L89.1043 16.8187H89.1055ZM88.4652 11.0265L88.8064 14.0902C88.81 14.1273 88.7835 14.1608 88.7475 14.1644L88.0772 14.247C88.0772 14.247 88.0711 14.247 88.0687 14.247C88.0507 14.247 88.0339 14.2399 88.0207 14.2279C88.0063 14.2135 87.9991 14.1932 88.0003 14.1728L88.3294 11.0265C88.333 10.9918 88.3619 10.9654 88.3967 10.9654C88.4315 10.9654 88.4604 10.9918 88.464 11.0253L88.4652 11.0265Z"
          fill="#FBF8F0"
        />
      </svg>
    </a>
  </Styled.Footer>
);
